import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { MusicKitEngine, PreviewEngine, type PlayerEngine } from '@karozik/music/player';
import { api, cn, Spinner, useParty } from '@karozik/ui';
import { Screen } from '../screen/Screen';
import { useAudioInputs, useVoiceDetection, useWakeLock } from './hooks';
import { usePlayerAudio } from './usePlayerAudio';

const MIC_KEY = 'karozik.micDevice';

function readMic(): string | null {
  try {
    return localStorage.getItem(MIC_KEY);
  } catch {
    return null;
  }
}

/**
 * /player/:id — runs fullscreen on the Mac mini: audio out through the default
 * output (Focusrite), visuals on the projector. One click is needed to start
 * (browsers only allow sound after a user gesture).
 */
export function Player() {
  const { id = '' } = useParams();
  const conn = useParty({ role: 'player', eventId: id });
  const [engine, setEngine] = useState<PlayerEngine | null>(null);
  const [started, setStarted] = useState(false);
  const [setupError, setSetupError] = useState<string | null>(null);
  const [micDevice, setMicDevice] = useState<string | null>(readMic);
  const provider = conn.state?.provider;
  const apple = useMusicKit(provider === 'apple');

  const ducking = conn.state?.staff?.ducking;
  const voice = useVoiceDetection(started && !!ducking?.auto, micDevice, ducking?.threshold ?? 0.08);
  const duck = !!conn.state?.mic.active || conn.state?.moment?.type === 'photo' || voice.speaking;
  const health = usePlayerAudio(conn, engine, started, duck);
  useWakeLock(started);
  const position = useCallback(() => engine?.positionMs() ?? 0, [engine]);

  useEffect(() => () => engine?.destroy(), [engine]);

  async function start() {
    setSetupError(null);
    try {
      let e: PlayerEngine;
      if (provider === 'apple') {
        if (!apple.engine || !apple.authorized) throw new Error('Connecte-toi d’abord à Apple Music.');
        e = apple.engine;
      } else {
        e = new PreviewEngine();
      }
      setEngine(e);
      setStarted(true);
      void document.documentElement.requestFullscreen?.().catch(() => undefined);
    } catch (err) {
      setSetupError((err as Error).message);
    }
  }

  if (!started) {
    return (
      <StartGate
        conn={conn}
        provider={provider}
        apple={apple}
        error={setupError ?? apple.error ?? conn.error}
        micDevice={micDevice}
        onMic={(d) => {
          setMicDevice(d);
          try {
            if (d) localStorage.setItem(MIC_KEY, d);
            else localStorage.removeItem(MIC_KEY);
          } catch {
            /* private mode */
          }
        }}
        onStart={() => void start()}
      />
    );
  }

  return (
    <>
      <Screen conn={conn} position={position} />
      <HealthBadge connected={conn.connected} offline={health.offline} offlinePlayed={health.offlinePlayed} error={health.lastError} provider={provider} speaking={voice.speaking} />
    </>
  );
}

interface AppleMusicSetup {
  engine: MusicKitEngine | null;
  authorized: boolean;
  error: string | null;
  authorize(): void;
}

/**
 * Configures MusicKit as soon as the page knows Apple Music is on, so the sign-in
 * click opens Apple's window right away: after a few awaits (token, script) the
 * browser would no longer count it as a click and block the popup. The session
 * is then remembered by the browser.
 */
function useMusicKit(enabled: boolean): AppleMusicSetup {
  const [engine, setEngine] = useState<MusicKitEngine | null>(null);
  const [authorized, setAuthorized] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initOnce = useRef<Promise<MusicKitEngine> | null>(null);

  useEffect(() => {
    if (!enabled) return;
    initOnce.current ??= (async () => {
      const { token } = await api<{ token: string }>('/api/admin/apple/developer-token');
      const mk = new MusicKitEngine();
      await mk.init(token);
      return mk;
    })();
    let live = true;
    initOnce.current.then(
      (mk) => {
        if (!live) return;
        setEngine(mk);
        setAuthorized(mk.isAuthorized());
      },
      (err: Error) => live && setError(`Apple Music : ${err.message}`),
    );
    return () => {
      live = false;
    };
  }, [enabled]);

  const authorize = useCallback(() => {
    if (!engine) return;
    setError(null);
    engine.authorize().then(
      () => setAuthorized(engine.isAuthorized()),
      () => setError('Connexion à Apple Music annulée ou refusée.'),
    );
  }, [engine]);

  return { engine, authorized, error, authorize };
}

function StartGate({
  conn,
  provider,
  apple,
  error,
  micDevice,
  onMic,
  onStart,
}: {
  conn: ReturnType<typeof useParty>;
  provider: string | undefined;
  apple: AppleMusicSetup;
  error: string | null;
  micDevice: string | null;
  onMic(d: string | null): void;
  onStart(): void;
}) {
  const inputs = useAudioInputs();
  const [askedMic, setAskedMic] = useState(false);
  const labelled = useMemo(() => inputs.filter((d) => d.label), [inputs]);

  return (
    <main className="mx-auto flex min-h-dvh max-w-xl flex-col justify-center gap-6 px-6">
      <div className="flex items-center gap-4">
        <img src="/icon.svg" alt="" className="animate-spin-slow h-16 w-16" />
        <div>
          <h1 className="font-display text-3xl font-bold">Lecteur & écran</h1>
          <p className="text-ink-dim">{conn.state?.event.name ?? 'Connexion…'}</p>
        </div>
      </div>

      <ol className="space-y-3 text-ink-dim">
        <li className="rounded-2xl bg-surface p-4">
          <b className="text-ink">1. Son</b> — la sortie audio par défaut du Mac doit être la <b>Focusrite</b> (Réglages système → Son → Sortie).
        </li>
        <li className="rounded-2xl bg-surface p-4">
          <b className="text-ink">2. Musique</b> —{' '}
          {provider === 'apple' ? (
            apple.authorized ? (
              <>
                Apple Music <b className="text-lime">connecté ✓</b> : morceaux complets.
              </>
            ) : (
              <span className="flex flex-wrap items-center gap-3">
                <span>connecte le compte Apple Music de la soirée (une seule fois sur ce Mac).</span>
                <button
                  onClick={apple.authorize}
                  disabled={!apple.engine}
                  className="flex items-center gap-2 rounded-xl bg-surface-2 px-3 py-2 text-sm text-ink disabled:opacity-40"
                >
                  {apple.engine ? 'Se connecter à Apple Music' : <Spinner />}
                </button>
              </span>
            )
          ) : (
            <>
              mode <b>extraits 30 s</b> (Apple Music pas encore configuré). Parfait pour tester et répéter.
            </>
          )}
        </li>
        <li className="space-y-2 rounded-2xl bg-surface p-4">
          <div>
            <b className="text-ink">3. Micro (facultatif)</b> — pour baisser la musique automatiquement quand quelqu’un parle, choisis l’entrée micro de la Focusrite.
          </div>
          {labelled.length === 0 ? (
            <button
              onClick={async () => {
                setAskedMic(true);
                const s = await navigator.mediaDevices.getUserMedia({ audio: true }).catch(() => null);
                s?.getTracks().forEach((t) => t.stop());
              }}
              className="rounded-xl bg-surface-2 px-3 py-2 text-sm text-ink"
            >
              {askedMic ? 'Autorisation demandée…' : 'Autoriser l’accès aux entrées audio'}
            </button>
          ) : (
            <select value={micDevice ?? ''} onChange={(e) => onMic(e.target.value || null)} className="input">
              <option value="">— Pas de détection automatique —</option>
              {labelled.map((d) => (
                <option key={d.deviceId} value={d.deviceId}>
                  {d.label}
                </option>
              ))}
            </select>
          )}
        </li>
      </ol>

      {error && <p className="rounded-xl bg-danger/15 px-4 py-3 text-danger">{error}</p>}
      <button
        onClick={onStart}
        disabled={!conn.state || (provider === 'apple' && !apple.authorized)}
        className="bg-gradient-neon flex items-center justify-center gap-3 rounded-3xl py-6 text-2xl font-bold shadow-2xl shadow-pink/30 disabled:opacity-40"
      >
        {conn.state ? '▶ Lancer la soirée' : <Spinner />}
      </button>
      <p className="text-center text-sm text-ink-faint">Astuce : garde cet onglet au premier plan, en plein écran, sur l’écran du projecteur.</p>
    </main>
  );
}

function HealthBadge({
  connected,
  offline,
  offlinePlayed,
  error,
  provider,
  speaking,
}: {
  connected: boolean;
  offline: boolean;
  offlinePlayed: number;
  error: string | null;
  provider: string | undefined;
  speaking: boolean;
}) {
  const ok = connected && !offline;
  return (
    <div
      className={cn(
        'fixed top-3 left-3 z-50 flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold transition-opacity',
        ok ? 'bg-black/40 text-ink-faint opacity-40 hover:opacity-100' : 'bg-danger text-white',
      )}
      title={error ?? undefined}
    >
      <span className={cn('h-2 w-2 rounded-full', ok ? 'bg-lime' : 'animate-pulse bg-white')} />
      {ok ? (provider === 'apple' ? 'Apple Music' : 'Extraits') : offline ? `Hors ligne : lecture autonome (${offlinePlayed})` : 'Reconnexion au serveur…'}
      {speaking && <span>· 🎤</span>}
    </div>
  );
}
